import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, HttpClient } from '@angular/common/http';
import { authInterceptor } from './core/auth.interceptor';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { importProvidersFrom } from '@angular/core';
import { Observable } from 'rxjs';
import { provideIcons } from '@ng-icons/core';
import { 
  heroInformationCircle, 
  heroGlobeAlt, 
  heroChevronDown,
  heroChevronLeft,
  heroChevronRight,
  heroPhone, 
  heroVideoCamera, 
  heroMapPin, 
  heroUser, 
  heroBars3,
  heroXMark,
  heroExclamationTriangle,
  heroCalendar,
  heroHome,
  heroCheckCircle,
  heroExclamationCircle,
  heroClock,
  heroCalendarDays,
  heroArrowPath,
  heroClipboardDocumentList,
  heroArrowLeftOnRectangle,
  heroArrowRightOnRectangle
} from '@ng-icons/heroicons/outline';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { ApiConfiguration } from './api/api-configuration';
import { TranslationService } from './core/translation.service';
import { CalendarContextService } from './core/calendar-context.service';


// Custom TranslateLoader
export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`/assets/i18n/${lang}.json`);
  }
}

// Factory for TranslateLoader
export function HttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(http);
}

// Factory for APP_INITIALIZER
export function initializeApp(translationService: TranslationService) {
  return () => translationService.initLanguage();
}

// Factory to eagerly instantiate CalendarContextService (start route listening)
export function initCalendarContext(calendarContextService: CalendarContextService) {
  return () => {
    // no-op: constructor wires Router.events; returning void is fine
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideIcons({
      heroInformationCircle,
      heroGlobeAlt,
      heroChevronDown,
      heroChevronLeft,
      heroChevronRight,
      heroPhone,
      heroVideoCamera,
      heroMapPin,
      heroUser,
      heroBars3,
      heroXMark,
      heroExclamationTriangle,
      heroCalendar,
      heroHome,
      heroCheckCircle,
      heroExclamationCircle,
      heroClock,
      heroCalendarDays,
      heroArrowPath,
      heroClipboardDocumentList,
      heroArrowLeftOnRectangle,
      heroArrowRightOnRectangle
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: ApiConfiguration,
      useFactory: () => {
        const config = new ApiConfiguration();
        config.rootUrl = environment.apiUrl;
        return config;
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [TranslationService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initCalendarContext,
      deps: [CalendarContextService],
      multi: true
    }
  ]
};
